<template>
    <div class="mt-2">
        <div>
            <div class="flex justify-between items-center mb-6">
                <div>
                    <h1 class="text-2xl font-bold text-gray-800 dark:text-white">Líderes do Setor: {{ sectorName }}</h1>
                    <p class="text-gray-600 dark:text-gray-400 mt-1">Gerenciamento de líderes deste setor</p>
                </div>
                <button 
                    @click="goBack" 
                    class="bg-gray-500 hover:bg-gray-600 text-white py-2 px-4 rounded-md flex items-center"
                >
                    <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
                    </svg>
                    Voltar
                </button>
            </div>
            
            <!-- Busca e adição de líderes -->
            <div class="mb-6 bg-gray-100 dark:bg-gray-900 p-4 rounded-lg">
                <h2 class="text-lg font-semibold mb-4 text-gray-800 dark:text-white">Adicionar Líder</h2>
                <div class="flex flex-col gap-4">
                    <div class="flex-grow" v-if="!selectedUser">
                        <users-select
                            v-model="selectedUser"
                            :multiple="false"
                            :return-object="true"
                            class="dark:text-white"
                        />
                    </div>
                    <div v-if="selectedUser" class="mt-2">
                        <div class="bg-gray-200 dark:bg-gray-600 p-3 rounded-md">
                            <div class="flex justify-between items-center">
                                <div>
                                    <div class="font-medium text-gray-800 dark:text-white">{{ selectedUser.name }}</div>
                                    <div class="text-sm text-gray-600 dark:text-gray-400">{{ selectedUser.email }}</div>
                                </div>
                                <button 
                                    @click="addLeader" 
                                    class="ml-4 bg-green-500 hover:bg-green-600 text-white py-1 px-3 rounded-md text-sm"
                                >
                                    Adicionar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            <!-- Lista de líderes atuais -->
            <div>
                <h2 class="text-lg font-semibold mb-4 text-gray-800 dark:text-white">Líderes Atuais</h2>
                <table-component
                    :loading="loading"
                    :headers="[
                        'Nome',
                        'Email',
                        'Ações'
                    ]"
                >
                    <tr v-for="leader in leaders" :key="leader.id" class="hover:bg-gray-100 dark:hover:bg-gray-700">
                        <td class="table-cell-sm">
                            <div class="flex items-center">
                                <div class="ml-3">
                                    <p class="text-gray-900 dark:text-white whitespace-no-wrap">
                                        {{ leader.name }}
                                    </p>
                                </div>
                            </div>
                        </td>
                        <td class="table-cell-sm">
                            <p class="text-gray-900 dark:text-white whitespace-no-wrap">
                                {{ leader.email }}
                            </p>
                        </td>
                        <td class="table-cell-sm">
                            <button 
                                @click="removeLeader(leader)" 
                                class="bg-red-500 hover:bg-red-600 text-white py-1 px-2 rounded-md text-xs"
                            >
                                Remover
                            </button>
                        </td>
                    </tr>
                    <tr v-if="leaders.length === 0">
                        <td colspan="3" class="table-cell-sm text-center text-gray-500 dark:text-gray-400">
                            Nenhum líder encontrado para este setor.
                        </td>
                    </tr>
                </table-component>
            </div>
        </div>
        
        <!-- Modal de confirmação para remover líder usando o componente Modal -->
        <modal :open="showRemoveConfirmModal" @close="showRemoveConfirmModal = false" size="sm">
            <div class="sm:flex sm:items-start">
                <div class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <svg class="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                </div>
                <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 class="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                        Remover Líder
                    </h3>
                    <div class="mt-2">
                        <p class="text-sm text-gray-500 dark:text-gray-400">
                            Tem certeza que deseja remover {{ currentLeader?.name }} como líder deste setor? 
                            Isso também removará as permissões de impersonate caso o usuário não seja líder de nenhum outro setor.
                        </p>
                    </div>
                </div>
            </div>
            <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button 
                    @click="confirmRemoveLeader" 
                    class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                    Remover
                </button>
                <button 
                    @click="showRemoveConfirmModal = false" 
                    class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                    Cancelar
                </button>
            </div>
        </modal>
    </div>
</template>

<script>
import Modal from "../../components/layouts/Modal.vue";
import TableComponent from "../../components/layouts/table/TableComponent.vue";
import UsersSelect from "../../components/forms/UsersSelect.vue";

export default {
    components: {
        Modal,
        TableComponent,
        UsersSelect
    },
    data() {
        return {
            sectorId: null,
            sectorName: '',
            leaders: [],
            selectedUser: null,
            currentLeader: null,
            showRemoveConfirmModal: false,
            loading: false
        }
    },
    
    mounted() {
        this.sectorId = this.$route.params.role_id;
        this.fetchSectorDetails();
        this.fetchLeaders();
    },
    
    methods: {
        fetchSectorDetails() {
            axios.get(`/api/sectors/${this.sectorId}`)
                .then(response => {
                    this.sectorName = response.data.role.name;
                })
                .catch(error => {
                    console.error('Erro ao carregar detalhes do setor:', error);
                    this.$notify({
                        group: 'error',
                        title: 'Erro',
                        text: 'Erro ao carregar detalhes do setor.'
                    }, 4000);
                });
        },
        
        fetchLeaders() {
            this.loading = true;
            axios.get(`/api/sectors/${this.sectorId}/leaders`)
                .then(response => {
                    this.leaders = response.data.leaders;
                    this.loading = false;
                })
                .catch(error => {
                    console.error('Erro ao carregar líderes:', error);
                    this.loading = false;
                    this.$notify({
                        group: 'error',
                        title: 'Erro',
                        text: 'Erro ao carregar líderes. Tente novamente mais tarde.'
                    }, 4000);
                });
        },
        
        addLeader() {
            if (!this.selectedUser) return;
            
            this.loading = true;
            axios.post(`/api/sectors/${this.sectorId}/leaders`, {
                user_id: this.selectedUser.id
            })
                .then(response => {
                    this.loading = false;
                    this.selectedUser = null;
                    this.fetchLeaders();
                    this.$notify({
                        group: 'success',
                        text: 'Líder adicionado com sucesso!'
                    }, 4000);
                })
                .catch(error => {
                    this.loading = false;
                    console.error('Erro ao adicionar líder:', error);
                    this.$notify({
                        group: 'error',
                        title: 'Erro',
                        text: error.response?.data?.message || 'Erro ao adicionar líder. Tente novamente mais tarde.'
                    }, 4000);
                });
        },
        
        removeLeader(leader) {
            this.currentLeader = leader;
            this.showRemoveConfirmModal = true;
        },
        
        confirmRemoveLeader() {
            this.loading = true;
            
            axios.delete(`/api/sectors/${this.sectorId}/leaders/${this.currentLeader.id}`)
                .then(response => {
                    this.loading = false;
                    this.showRemoveConfirmModal = false;
                    this.fetchLeaders();
                    this.$notify({
                        group: 'success',
                        text: 'Líder removido com sucesso!'
                    }, 4000);
                })
                .catch(error => {
                    this.loading = false;
                    this.showRemoveConfirmModal = false;
                    console.error('Erro ao remover líder:', error);
                    this.$notify({
                        group: 'error',
                        title: 'Erro',
                        text: error.response?.data?.message || 'Erro ao remover líder. Tente novamente mais tarde.'
                    }, 4000);
                });
        },
        
        goBack() {
            this.$router.push({ name: 'sectors' });
        }
    }
}
</script> 